'use client'

import { useEffect, useState, useCallback } from 'react'

/**
 * Playlists VIDÉO (BLC Video Player) — persistées en localStorage.
 * Simple et fiable ; on pourra basculer sur la BDD plus tard si besoin de
 * synchro multi-appareils.
 */
export type VideoItem = { videoId: string; title: string; channel?: string; thumbnail?: string; duration?: string }
export type VideoPlaylist = { id: string; name: string; items: VideoItem[]; createdAt: number }

const KEY = 'blc.video.playlists.v1'

function read(): VideoPlaylist[] {
  if (typeof localStorage === 'undefined') return []
  try { return JSON.parse(localStorage.getItem(KEY) || '[]') } catch { return [] }
}
function write(list: VideoPlaylist[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); window.dispatchEvent(new Event('blc-video-playlists')) } catch {}
}
function uid() { return 'vp_' + Math.random().toString(36).slice(2, 9) + Date.now().toString(36) }

export function useVideoPlaylists() {
  const [lists, setLists] = useState<VideoPlaylist[]>([])
  useEffect(() => {
    const load = () => setLists(read())
    load()
    window.addEventListener('blc-video-playlists', load)
    window.addEventListener('storage', load)
    return () => { window.removeEventListener('blc-video-playlists', load); window.removeEventListener('storage', load) }
  }, [])

  const create = useCallback((name: string): string => {
    const list = read()
    const pl: VideoPlaylist = { id: uid(), name: name.trim() || 'Sans titre', items: [], createdAt: Date.now() }
    write([pl, ...list])
    return pl.id
  }, [])

  const remove = useCallback((id: string) => { write(read().filter(p => p.id !== id)) }, [])

  const addTo = useCallback((id: string, item: VideoItem) => {
    const list = read()
    const p = list.find(x => x.id === id); if (!p) return
    if (!p.items.some(i => i.videoId === item.videoId)) p.items.unshift(item)
    write(list)
  }, [])

  const removeFrom = useCallback((id: string, videoId: string) => {
    const list = read()
    const p = list.find(x => x.id === id); if (!p) return
    p.items = p.items.filter(i => i.videoId !== videoId)
    write(list)
  }, [])

  const has = useCallback((id: string, videoId: string) => {
    return read().find(x => x.id === id)?.items.some(i => i.videoId === videoId) ?? false
  }, [])

  return { lists, create, remove, addTo, removeFrom, has }
}
